'use client';

import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { Cookie } from '../model';
import { createSafeContext } from './createSafeContext';
import { getCartSessionKey as getCartSessionKeyUtils } from '../utils';
type CartSessionKeyContextValue = {
  sessionKey?: Cookie;
  setSessionKey: (value: Cookie) => void;
  clearSessionKey: () => void;
};
const [CartSessionKeyContextProvider, useCartSessionKeyContext] = createSafeContext<CartSessionKeyContextValue>('CartSessionKey');
type CartSessionKeyProviderProps = {
  children: ReactNode;
  getCartSessionKey?: () => {
    cartSessionKey: Cookie | undefined;
  } | Promise<{
    cartSessionKey: Cookie | undefined;
  }>;
};
export const CartSessionKeyProvider = ({
  children,
  getCartSessionKey: _getCartSessionKey
}: CartSessionKeyProviderProps) => {
  const getCartSessionKey = _getCartSessionKey ?? getCartSessionKeyUtils;
  const [sessionKey, setInternalSessionKey] = useState<Cookie | undefined>();
  const setSessionKey = useCallback((props: Cookie) => {
    setInternalSessionKey(prev => ({
      ...prev,
      ...props
    }));
  }, []);
  const clearSessionKey = useCallback(() => {
    setInternalSessionKey({
      name: undefined,
      value: undefined
    });
  }, []);
  const contextValue = useMemo(() => ({
    sessionKey,
    setSessionKey,
    clearSessionKey
  }), [sessionKey, setSessionKey, clearSessionKey]);
  useEffect(() => {
    (async () => {
      const {
        cartSessionKey
      } = await getCartSessionKey();
      if (cartSessionKey) {
        setSessionKey(cartSessionKey);
      }
    })();
  }, [setSessionKey]);
  return <CartSessionKeyContextProvider value={contextValue} data-sentry-element="CartSessionKeyContextProvider" data-sentry-component="CartSessionKeyProvider" data-sentry-source-file="cartSessionKeyContext.tsx">{children}</CartSessionKeyContextProvider>;
};
export const useCartSessionKey = useCartSessionKeyContext;